import { useState, useMemo, useEffect } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import type { BarSeriesType } from '@mui/x-charts';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import type { TimeEntry, Department } from '@altyor-kronos/shared-types';
import { useCollectionSnapshot } from '../core/data/useCollectionSnapshot';
import {
  useAdminEntriesInRange,
  useAllClients,
  useAllDesignations,
  useAllProjects,
  useAllUsers,
  useActiveTags,
} from '../features/kronos/useKronosData';
import { DateRangePicker } from '../components/DateRangePicker';
import { presetRange, type DateRange } from '../features/kronos/reportDateRange';
import { formatDuration, formatHoursDecimal } from '../features/kronos/reportFormat';
import {
  buildDailyDetails,
  buildDailyUserSeries,
  topSeriesOfDay,
  type DayDetail,
} from '../features/kronos/reportDailyDetails';
import DayBreakdownTooltip from '../features/kronos/DayBreakdownTooltip';
import { buildDesignationNameOptions } from '../features/kronos/reportDesignationFilter';
import { useAuth } from '../core/auth/useAuth';
import { canExportDetailedReport } from '../core/auth/reportExportAccess';
import { previousMonthKey } from '@altyor-kronos/shared-types';
import { useTimeLock, setTimeLock } from '../features/kronos/useTimeLock';
import { nonWorkingDay } from '../features/kronos/workingDays';
import CloseMonthDialog from '../features/kronos/CloseMonthDialog';
import { groupFromRoles } from '../features/kronos/reportGroupFromRoles';
import {
  buildDetailedReportRows,
  buildDetailedReportWorkbook,
  type DetailedReportMaps,
} from '../features/kronos/detailedReportExport';
import { colors } from '../core/theme/theme';
import { PROJECT_COLORS } from '../features/kronos/projectColors';

// ─── Types ─────────────────────────────────────────────────────────

type ViewTab = 'summary' | 'detailed' | 'weekly';

/** Tint marking a weekend or public holiday across every report tab. */
const NON_WORKING_BG = 'rgba(237, 237, 237, 0.45)';
type GroupBy = 'project' | 'client' | 'user';
type BillableFilter = 'all' | 'billable' | 'non-billable';

// ─── Pure date helpers ──────────────────────────────────────────────

function formatDayLabel(d: Date, locale: string): string {
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' });
}

// ─── Main component ─────────────────────────────────────────────────

export default function KronosReport() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { user, account } = useAuth();

  // ── UI state ─────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ViewTab>('summary');
  const [groupBy, setGroupBy] = useState<GroupBy>('project');
  const [filterUsers, setFilterUsers] = useState<string[]>([]);
  const [filterGroups, setFilterGroups] = useState<string[]>([]);
  const [filterClients, setFilterClients] = useState<string[]>([]);
  const [filterProjects, setFilterProjects] = useState<string[]>([]);
  const [filterDesignations, setFilterDesignations] = useState<string[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterBillable, setFilterBillable] = useState<BillableFilter>('all');

  // ── Date range ────────────────────────────────────────────────────
  const [range, setRange] = useState<DateRange>(() => presetRange('thisWeek', new Date()));
  const { from, to } = range;

  // ── Data subscriptions ────────────────────────────────────────────
  const { entries: rawEntries, loading, error } = useAdminEntriesInRange(from, to, true);
  const { users: userRows } = useAllUsers(true);
  const { clients: clientsById } = useAllClients();
  const { projects: projectRows } = useAllProjects();
  const { designations: allDesignations } = useAllDesignations(true);
  const { rows: departmentRows } = useCollectionSnapshot<Department>('departments');

  // ── Lookup maps ───────────────────────────────────────────────────
  const projectsById = useMemo(() => {
    const m = new Map<string, { code: string; name: string; clientId: string; allMemberUids: string[] }>();
    for (const p of projectRows) m.set(p.id, { code: p.data.code, name: p.data.name, clientId: p.data.clientId, allMemberUids: p.data.allMemberUids ?? [] });
    return m;
  }, [projectRows]);

  const usersById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of userRows) m.set(u.id, u.data.displayName ?? u.id);
    return m;
  }, [userRows]);

  const userDepartmentById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of userRows) m.set(u.id, u.data.departmentId ?? '');
    return m;
  }, [userRows]);

  const departmentsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departmentRows) m.set(d.id, d.data.name);
    return m;
  }, [departmentRows]);

  const groupOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const u of userRows) {
      const g = u.data.departmentId;
      if (g) ids.add(g);
    }
    return [...ids]
      .map((id) => ({ id, label: departmentsById.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [userRows, departmentsById]);

  const groupScopedUsers = useMemo(() => {
    const scoped = filterGroups.length === 0
      ? userRows
      : userRows.filter((u) => filterGroups.includes(u.data.departmentId ?? ''));
    return scoped
      .slice()
      .sort((a, b) => (a.data.displayName ?? '').localeCompare(b.data.displayName ?? ''));
  }, [userRows, filterGroups]);

  const designationNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of allDesignations) m.set(d.id, d.data.name);
    return m;
  }, [allDesignations]);

  // ── Detailed Report export (admin / finance / allowlist) ──────────
  const canExport = canExportDetailedReport(user, account);

  // projectsById on this page carries extra fields; narrow it for the exporter.
  const exportProjectsById = useMemo(() => {
    const m = new Map<string, { code: string; name: string; clientId: string }>();
    for (const [id, p] of projectsById) m.set(id, { code: p.code, name: p.name, clientId: p.clientId });
    return m;
  }, [projectsById]);

  const clientNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const [id, c] of clientsById) m.set(id, c.name);
    return m;
  }, [clientsById]);

  const userGroupById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of userRows) m.set(u.id, groupFromRoles(u.data.roles ?? []));
    return m;
  }, [userRows]);

  // ── Filtered entries ──────────────────────────────────────────────
  // Entries passing every filter EXCEPT designation — drives both the
  // designation dropdown options and the final filtered list.
  const scopedEntries = useMemo<TimeEntry[]>(() => {
    let entries = rawEntries.map((e) => e.data);
    if (filterGroups.length > 0) {
      entries = entries.filter((e) =>
        filterGroups.includes(userDepartmentById.get(e.userId) ?? ''),
      );
    }
    if (filterUsers.length > 0) {
      entries = entries.filter((e) => filterUsers.includes(e.userId));
    }
    if (filterProjects.length > 0) {
      entries = entries.filter((e) => filterProjects.includes(e.projectId));
    }
    if (filterClients.length > 0) {
      entries = entries.filter((e) => {
        const p = projectsById.get(e.projectId);
        return p ? filterClients.includes(p.clientId) : false;
      });
    }
    if (filterBillable === 'billable') entries = entries.filter((e) => e.billable);
    if (filterBillable === 'non-billable') entries = entries.filter((e) => !e.billable);
    return entries;
  }, [rawEntries, filterGroups, filterUsers, filterProjects, filterClients, filterBillable, projectsById, userDepartmentById]);

  const designationOptions = useMemo(
    () => buildDesignationNameOptions(scopedEntries, designationNameById),
    [scopedEntries, designationNameById],
  );

  // Tag options come from the curated catalogue (not just tags present in the
  // current date range) so the filter always offers the full list — matching
  // how Clockify shows every tag.
  const tagOptions = useActiveTags();

  // Designation + tag options depend on the other filters; drop selected
  // values that are no longer offered when the user narrows team/client/project.
  useEffect(() => {
    setFilterDesignations((prev) => {
      const available = new Set(designationOptions);
      const next = prev.filter((n) => available.has(n));
      return next.length === prev.length ? prev : next;
    });
  }, [designationOptions]);

  useEffect(() => {
    setFilterTags((prev) => {
      const available = new Set(tagOptions);
      const next = prev.filter((tg) => available.has(tg));
      return next.length === prev.length ? prev : next;
    });
  }, [tagOptions]);

  const filtered = useMemo<TimeEntry[]>(() => {
    let entries = scopedEntries;
    if (filterDesignations.length > 0) {
      const selected = new Set(filterDesignations);
      entries = entries.filter((e) => {
        const name = designationNameById.get(e.designationId);
        return name !== undefined && selected.has(name);
      });
    }
    if (filterTags.length > 0) {
      const selected = new Set(filterTags);
      entries = entries.filter((e) => (e.tags ?? []).some((tg) => selected.has(tg)));
    }
    return entries;
  }, [scopedEntries, filterDesignations, filterTags, designationNameById]);

  // ── Clôture mensuelle ────────────────────────────────────────────
  // Poser le verrou est réservé à admin|finance côté kronos-api. L'allowlist
  // e-mail de l'export est plus large : si on se calait dessus, on afficherait
  // une case qui produit un 403 (le piège déjà tombé deux fois, cf. CLAUDE.md).
  const canCloseMonth = (user?.roles ?? []).some((r) => r === 'admin' || r === 'finance');
  const { lockedThrough } = useTimeLock();
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const monthToClose = previousMonthKey(new Date());

  const downloadDetailed = () => {
    const maps: DetailedReportMaps = {
      projectsById: exportProjectsById,
      clientNameById,
      userDisplayNameById: usersById,
      userGroupById,
      designationNameById,
    };
    // Exports exactly what the screen shows: every filter, not just the period.
    const rows = buildDetailedReportRows(filtered, maps);
    const wb = buildDetailedReportWorkbook(rows);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Detailed Report ${fmt(from)}-${fmt(to)}.xlsx`);
  };

  // Qui peut clôturer passe par le modal ; les autres téléchargent directement.
  const handleExportDetailed = () => {
    setCloseError(null);
    if (canCloseMonth) { setCloseDialogOpen(true); return; }
    downloadDetailed();
  };

  // L'export part dans tous les cas — un échec de clôture ne doit pas priver
  // la finance de son fichier ; on le lui dit et elle réessaie.
  const handleCloseDialogConfirm = async (close: boolean) => {
    setCloseDialogOpen(false);
    downloadDetailed();
    if (!close) return;
    try {
      await setTimeLock(monthToClose);
    } catch (e) {
      setCloseError((e as Error).message);
    }
  };

  const totalSeconds = useMemo(
    () => filtered.reduce((s, e) => s + (e.durationSeconds ?? 0), 0),
    [filtered],
  );

  // ── Helper: get group key + label ─────────────────────────────────
  const getGroupKey = (e: TimeEntry): string => {
    if (groupBy === 'user') return e.userId;
    if (groupBy === 'client') {
      const p = projectsById.get(e.projectId);
      return p?.clientId ?? 'unknown';
    }
    return e.projectId;
  };

  const getLabelForKey = (key: string): string => {
    if (groupBy === 'user') return usersById.get(key) ?? key;
    if (groupBy === 'client') {
      const c = clientsById.get(key);
      return c?.name ?? key;
    }
    const p = projectsById.get(key);
    return p ? `${p.code} — ${p.name}` : key;
  };

  // ── Summary: grouped rows ─────────────────────────────────────────
  const groupedRows = useMemo(() => {
    const m = new Map<string, { totalSecs: number; billableSecs: number }>();
    for (const e of filtered) {
      const k = getGroupKey(e);
      const cur = m.get(k) ?? { totalSecs: 0, billableSecs: 0 };
      cur.totalSecs += e.durationSeconds ?? 0;
      cur.billableSecs += e.billable ? (e.durationSeconds ?? 0) : 0;
      m.set(k, cur);
    }
    return [...m.entries()]
      .map(([key, val]) => ({
        key,
        label: getLabelForKey(key),
        ...val,
        percent: totalSeconds > 0 ? (val.totalSecs / totalSeconds) * 100 : 0,
      }))
      .sort((a, b) => b.totalSecs - a.totalSecs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, groupBy, projectsById, usersById, clientsById, totalSeconds]);

  // ── Summary: daily bar chart data ─────────────────────────────────
  // ── Summary: per-day breakdown (drives both the bars and the hover detail) ──
  const dailyDetails = useMemo<DayDetail[]>(
    () =>
      buildDailyDetails(filtered, from, to, {
        projectsById,
        designationNameById,
        usersById,
      }),
    [filtered, from, to, projectsById, designationNameById, usersById],
  );

  // ── Summary: daily bar chart data (derived from dailyDetails, same order) ──
  const dailyData = useMemo(
    () =>
      dailyDetails.map((d) => ({
        label: formatDayLabel(d.date, locale),
        hours: d.totalSeconds / 3600,
      })),
    [dailyDetails, locale],
  );

  // One bar per day, split by user (with a legend) as soon as several users are
  // selected. Whatever the split, the day total is printed above the column.
  const chartSeries = useMemo<BarSeriesType[]>(() => {
    const dayTotals = dailyDetails.map((d) => formatHoursDecimal(d.totalSeconds));
    const perUser = filterUsers.length > 1 ? buildDailyUserSeries(dailyDetails) : [];

    if (perUser.length === 0) {
      // Weekends and public holidays get their own grey series so the bar
      // itself reads as "this was not a working day". Two stacked series
      // index by position — an axis colorMap would key on the tick label,
      // which repeats once the range spans more than a year.
      const offDay = dailyDetails.map((d) => nonWorkingDay(d.date) !== null);
      const hours = dailyDetails.map((d) => d.totalSeconds / 3600);
      const workingSeries: BarSeriesType = {
        type: 'bar',
        data: hours.map((h, i) => (offDay[i] ? 0 : h)),
        label: t('kronos.report.chartHours'),
        color: colors.yellow,
        stack: 'day',
        barLabel: (item) => (item.value ? dayTotals[item.dataIndex] : null),
        barLabelPlacement: 'outside',
      };
      if (!offDay.some(Boolean)) return [workingSeries];
      return [
        workingSeries,
        {
          type: 'bar',
          data: hours.map((h, i) => (offDay[i] ? h : 0)),
          label: t('kronos.report.chartNonWorking'),
          color: colors.lightGray,
          stack: 'day',
          barLabel: (item) => (item.value ? dayTotals[item.dataIndex] : null),
          barLabelPlacement: 'outside',
        },
      ];
    }

    // Only the topmost segment of a day carries the label, so the total lands
    // above the whole stack instead of once per user.
    const topOfDay = topSeriesOfDay(perUser, dailyDetails.length);

    return perUser.map((s, i) => ({
      type: 'bar',
      data: s.hours,
      label: s.userName,
      color: PROJECT_COLORS[i % PROJECT_COLORS.length],
      stack: 'day',
      barLabel: (item) => (topOfDay[item.dataIndex] === i ? dayTotals[item.dataIndex] : null),
      barLabelPlacement: 'outside',
    }));
  }, [dailyDetails, filterUsers, t]);

  // ── Weekly tab: day columns ───────────────────────────────────────
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let i = 0;
    while (cur <= to && i < 7) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
      i++;
    }
    return days;
  }, [from, to]);

  const weeklyMatrix = useMemo(() => {
    const m = new Map<string, { label: string; byDay: Map<number, number>; total: number }>();
    for (const e of filtered) {
      const t = e.startTime.toDate();
      const dayKey = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
      const isDayInRange = weekDays.some((d) => d.getTime() === dayKey);
      if (!isDayInRange) continue;
      const gk = getGroupKey(e);
      let row = m.get(gk);
      if (!row) {
        row = { label: getLabelForKey(gk), byDay: new Map(), total: 0 };
        m.set(gk, row);
      }
      const secs = e.durationSeconds ?? 0;
      row.byDay.set(dayKey, (row.byDay.get(dayKey) ?? 0) + secs);
      row.total += secs;
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, weekDays, groupBy, projectsById, usersById, clientsById]);

  // ── Filter helpers ────────────────────────────────────────────────
  const hasActiveFilters =
    filterUsers.length > 0 ||
    filterGroups.length > 0 ||
    filterClients.length > 0 ||
    filterProjects.length > 0 ||
    filterDesignations.length > 0 ||
    filterTags.length > 0 ||
    filterBillable !== 'all';

  const clearFilters = () => {
    setFilterUsers([]);
    setFilterGroups([]);
    setFilterClients([]);
    setFilterProjects([]);
    setFilterDesignations([]);
    setFilterTags([]);
    setFilterBillable('all');
  };

  // ── Cascading filter options: Customer → Project ─────────────────
  // The team filter deliberately does NOT narrow projects: anyone can log time
  // on any project without being a member, so membership says nothing about
  // where a person's hours are.

  // Customers: only if the client doc exists
  const clientOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const p of projectRows) ids.add(p.data.clientId);
    return [...ids]
      .filter((id) => clientsById.has(id))
      .map((id) => ({ id, name: clientsById.get(id)!.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projectRows, clientsById]);

  // Projects: scoped to selected customers
  const availableProjectRows = useMemo(() => {
    if (filterClients.length === 0) return projectRows;
    return projectRows.filter((p) => filterClients.includes(p.data.clientId));
  }, [projectRows, filterClients]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <Box data-testid="kronos-report">
      {/* Hero header with background image */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mx: { xs: -2, sm: -3, md: -5 },
          mt: { xs: -2, sm: -3, md: -5 },
          mb: 3,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, sm: 4 },
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          component="img"
          src="/images/report-header.webp"
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(20, 23, 28, 0.55)',
          }}
        />
        <Stack spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ color: colors.white, fontWeight: 700 }}>
            {t('kronos.report.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {t('kronos.report.subtitle')}
          </Typography>
        </Stack>
      </Box>

      {/* ── Date range navigator ──────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          borderColor: colors.lightGray,
          borderRadius: 0,
          px: 2,
          py: 1.5,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <DateRangePicker value={range} onChange={setRange} locale={locale} />
        {canExport && (
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleExportDetailed}
            disabled={loading}
            sx={{ ml: 'auto' }}
          >
            {t('kronos.report.exportDetailed')}
          </Button>
        )}
      </Paper>

      {closeError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCloseError(null)}>
          {t('kronos.report.closeMonth.failed', { detail: closeError })}
        </Alert>
      )}

      <CloseMonthDialog
        open={closeDialogOpen}
        monthToClose={monthToClose}
        lockedThrough={lockedThrough}
        onCancel={() => setCloseDialogOpen(false)}
        onConfirm={handleCloseDialogConfirm}
      />

      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          borderColor: colors.lightGray,
          borderRadius: 0,
          px: 2,
          py: 1.5,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Group filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 160 } }} data-testid="report-group-filter">
          <InputLabel sx={{ fontSize: '13px' }}>{t('kronos.report.filters.group')}</InputLabel>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Select<any>
            multiple
            value={filterGroups}
            label={t('kronos.report.filters.group')}
            onChange={(e) => {
              const v = e.target.value as string[];
              let next: string[];
              if (v.includes('__all__')) {
                next = filterGroups.length === groupOptions.length ? [] : groupOptions.map((g) => g.id);
              } else {
                next = v;
              }
              setFilterGroups(next);
              // cascade: prune user selections no longer in any selected group
              if (next.length > 0) {
                setFilterUsers((prev) =>
                  prev.filter((uid) => next.includes(userDepartmentById.get(uid) ?? '')),
                );
              }
            }}
            renderValue={(selected: string[]) =>
              selected.length === 0
                ? ''
                : selected.length === groupOptions.length && groupOptions.length > 1
                  ? t('kronos.report.filters.allGroups')
                  : selected.length === 1
                    ? (departmentsById.get(selected[0]) ?? selected[0])
                    : t('kronos.report.filters.groupsCount', { count: selected.length })
            }
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="__all__" sx={{ fontSize: '13px', fontWeight: 700 }}>
              <Checkbox size="small" checked={filterGroups.length === groupOptions.length && groupOptions.length > 0} indeterminate={filterGroups.length > 0 && filterGroups.length < groupOptions.length} />
              {t('kronos.report.filters.selectAll')}
            </MenuItem>
            <Divider />
            {groupOptions.map((g) => (
              <MenuItem key={g.id} value={g.id} sx={{ fontSize: '13px' }}>
                <Checkbox size="small" checked={filterGroups.includes(g.id)} />
                {g.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Team (user) filter — typeahead, surname-first alphabetical */}
        <Autocomplete
          multiple
          size="small"
          data-testid="report-user-filter"
          sx={{ minWidth: { xs: 160, sm: 220 } }}
          options={groupScopedUsers}
          value={groupScopedUsers.filter((u) => filterUsers.includes(u.id))}
          getOptionLabel={(u) => u.data.displayName ?? u.id}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, selected) => setFilterUsers(selected.map((u) => u.id))}
          limitTags={1}
          renderInput={(params) => (
            <TextField {...params} label={t('kronos.report.filters.team')} />
          )}
        />

        {/* Customer filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 160 } }}>
          <InputLabel sx={{ fontSize: '13px' }}>{t('kronos.report.filters.customer')}</InputLabel>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Select<any>
            multiple
            value={filterClients}
            label={t('kronos.report.filters.customer')}
            onChange={(e) => {
              const v = e.target.value as string[];
              const allIds = clientOptions.map((c) => c.id);
              let newClients: string[];
              if (v.includes('__all__')) {
                newClients = filterClients.length === allIds.length ? [] : allIds;
              } else {
                newClients = v;
              }
              setFilterClients(newClients);
              // cascade: prune project selections
              if (newClients.length > 0) {
                setFilterProjects((prev) =>
                  prev.filter((pid) => {
                    const p = projectsById.get(pid);
                    return p ? newClients.includes(p.clientId) : false;
                  }),
                );
              }
            }}
            renderValue={(selected: string[]) =>
              selected.length === 0
                ? ''
                : selected.length === clientOptions.length && clientOptions.length > 1
                  ? t('kronos.report.filters.allCustomers')
                  : selected.length === 1
                    ? (clientsById.get(selected[0])?.name ?? selected[0])
                    : t('kronos.report.filters.customersCount', { count: selected.length })
            }
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="__all__" sx={{ fontSize: '13px', fontWeight: 700 }}>
              <Checkbox size="small" checked={filterClients.length === clientOptions.length && clientOptions.length > 0} indeterminate={filterClients.length > 0 && filterClients.length < clientOptions.length} />
              {t('kronos.report.filters.selectAll')}
            </MenuItem>
            <Divider />
            {clientOptions.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={{ fontSize: '13px' }}>
                <Checkbox size="small" checked={filterClients.includes(c.id)} />
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Project filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 180 } }}>
          <InputLabel sx={{ fontSize: '13px' }}>{t('kronos.report.filters.project')}</InputLabel>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Select<any>
            multiple
            value={filterProjects}
            label={t('kronos.report.filters.project')}
            onChange={(e) => {
              const v = e.target.value as string[];
              const allIds = availableProjectRows.map((p) => p.id);
              if (v.includes('__all__')) {
                setFilterProjects(filterProjects.length === allIds.length ? [] : allIds);
              } else {
                setFilterProjects(v);
              }
            }}
            renderValue={(selected: string[]) =>
              selected.length === 0
                ? ''
                : selected.length === availableProjectRows.length && availableProjectRows.length > 1
                  ? t('kronos.report.filters.allProjects')
                  : selected.length === 1
                    ? (projectsById.get(selected[0])
                        ? `${projectsById.get(selected[0])!.code}`
                        : selected[0])
                    : t('kronos.report.filters.projectsCount', { count: selected.length })
            }
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="__all__" sx={{ fontSize: '13px', fontWeight: 700 }}>
              <Checkbox size="small" checked={filterProjects.length === availableProjectRows.length && availableProjectRows.length > 0} indeterminate={filterProjects.length > 0 && filterProjects.length < availableProjectRows.length} />
              {t('kronos.report.filters.selectAll')}
            </MenuItem>
            <Divider />
            {availableProjectRows
              .slice()
              .sort((a, b) => a.data.code.localeCompare(b.data.code))
              .map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: '13px' }}>
                  <Checkbox size="small" checked={filterProjects.includes(p.id)} />
                  <span style={{ fontWeight: 700, marginRight: 6 }}>{p.data.code}</span>{p.data.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Designation filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 180 } }}>
          <InputLabel sx={{ fontSize: '13px' }}>{t('kronos.report.filters.designation')}</InputLabel>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Select<any>
            multiple
            value={filterDesignations}
            label={t('kronos.report.filters.designation')}
            onChange={(e) => {
              const v = e.target.value as string[];
              if (v.includes('__all__')) {
                setFilterDesignations(
                  filterDesignations.length === designationOptions.length ? [] : designationOptions,
                );
              } else {
                setFilterDesignations(v);
              }
            }}
            renderValue={(selected: string[]) =>
              selected.length === 0
                ? ''
                : selected.length === designationOptions.length && designationOptions.length > 1
                  ? t('kronos.report.filters.allDesignations')
                  : selected.length === 1
                    ? selected[0]
                    : t('kronos.report.filters.designationsCount', { count: selected.length })
            }
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="__all__" sx={{ fontSize: '13px', fontWeight: 700 }}>
              <Checkbox
                size="small"
                checked={filterDesignations.length === designationOptions.length && designationOptions.length > 0}
                indeterminate={filterDesignations.length > 0 && filterDesignations.length < designationOptions.length}
              />
              {t('kronos.report.filters.selectAll')}
            </MenuItem>
            <Divider />
            {designationOptions.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontSize: '13px' }}>
                <Checkbox size="small" checked={filterDesignations.includes(name)} />
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Tag filter */}
        <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 180 } }}>
          <InputLabel sx={{ fontSize: '13px' }}>{t('kronos.report.filters.tag')}</InputLabel>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Select<any>
            multiple
            value={filterTags}
            label={t('kronos.report.filters.tag')}
            onChange={(e) => {
              const v = e.target.value as string[];
              if (v.includes('__all__')) {
                setFilterTags(filterTags.length === tagOptions.length ? [] : tagOptions);
              } else {
                setFilterTags(v);
              }
            }}
            renderValue={(selected: string[]) =>
              selected.length === 0
                ? ''
                : selected.length === tagOptions.length && tagOptions.length > 1
                  ? t('kronos.report.filters.allTags')
                  : selected.length === 1
                    ? selected[0]
                    : t('kronos.report.filters.tagsCount', { count: selected.length })
            }
            sx={{ fontSize: '13px' }}
          >
            <MenuItem value="__all__" sx={{ fontSize: '13px', fontWeight: 700 }}>
              <Checkbox
                size="small"
                checked={filterTags.length === tagOptions.length && tagOptions.length > 0}
                indeterminate={filterTags.length > 0 && filterTags.length < tagOptions.length}
              />
              {t('kronos.report.filters.selectAll')}
            </MenuItem>
            <Divider />
            {tagOptions.map((tg) => (
              <MenuItem key={tg} value={tg} sx={{ fontSize: '13px' }}>
                <Checkbox size="small" checked={filterTags.includes(tg)} />
                {tg}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Billable filter */}
        <Stack direction="row" spacing={0.5}>
          {(['all', 'billable', 'non-billable'] as BillableFilter[]).map((b) => (
            <Chip
              key={b}
              label={b === 'all' ? t('kronos.report.filters.billableAll') : b === 'billable' ? t('kronos.report.filters.billable') : t('kronos.report.filters.nonBillable')}
              size="small"
              onClick={() => setFilterBillable(b)}
              sx={{
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '12px',
                bgcolor: filterBillable === b ? colors.darkBg : colors.lightGray,
                color: filterBillable === b ? colors.white : colors.textMuted,
                '&:hover': {
                  bgcolor: filterBillable === b ? colors.darkBg : colors.peach,
                },
              }}
            />
          ))}
        </Stack>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Typography
            variant="caption"
            onClick={clearFilters}
            sx={{
              color: colors.red,
              cursor: 'pointer',
              fontWeight: 600,
              ml: { xs: 0, sm: 'auto' },
              alignSelf: 'center',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('kronos.report.filters.clear')}
          </Typography>
        )}
      </Paper>

      {/* ── Error / Loading ───────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t('kronos.report.loading')}
          </Typography>
        </Box>
      )}

      {/* ── KPI + Group by row ────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { xs: 'stretch', md: 'center' }, justifyContent: { md: 'space-between' }, mb: 2 }}
      >
        <Paper
          elevation={0}
          sx={{ bgcolor: colors.darkBg, color: colors.white, px: 3, py: 2, borderRadius: 0, display: 'inline-block' }}
        >
          <Typography variant="caption" sx={{ color: colors.lightGray, display: 'block', mb: 0.5 }}>
            {t('kronos.report.total')}
          </Typography>
          <Typography sx={{ fontSize: '28px', fontWeight: 900, lineHeight: 1 }}>
            {formatHoursDecimal(totalSeconds)}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.kronosAccent }}>
            {t('kronos.report.entriesCount', { count: filtered.length })}
          </Typography>
        </Paper>

        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {t('kronos.report.groupBy')}
          </Typography>
          <FormControl size="small">
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              sx={{ fontSize: '13px', minWidth: 120 }}
            >
              <MenuItem value="project" sx={{ fontSize: '13px' }}>{t('kronos.report.groupOptions.project')}</MenuItem>
              <MenuItem value="client" sx={{ fontSize: '13px' }}>{t('kronos.report.groupOptions.customer')}</MenuItem>
              <MenuItem value="user" sx={{ fontSize: '13px' }}>{t('kronos.report.groupOptions.user')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {/* ── Tabs ──────────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onChange={(_, v: ViewTab) => setActiveTab(v)}
        sx={{ mb: 0, borderBottom: `2px solid ${colors.lightGray}` }}
      >
        <Tab label={t('kronos.report.tabs.summary')} value="summary" />
        <Tab label={t('kronos.report.tabs.detailed')} value="detailed" />
        <Tab label={t('kronos.report.tabs.weekly')} value="weekly" />
      </Tabs>

      {/* ── Tab content ───────────────────────────────────────────── */}
      {activeTab === 'summary' && (
        <SummaryView
          dailyData={dailyData}
          chartSeries={chartSeries}
          dailyDetails={dailyDetails}
          groupedRows={groupedRows}
          totalSeconds={totalSeconds}
        />
      )}

      {activeTab === 'detailed' && (
        <DetailedView
          entries={filtered}
          projectsById={projectsById}
          usersById={usersById}
          designationNameById={designationNameById}
          locale={locale}
        />
      )}

      {activeTab === 'weekly' && (
        <WeeklyView
          weekDays={weekDays}
          rows={weeklyMatrix}
          totalSeconds={totalSeconds}
          locale={locale}
        />
      )}
    </Box>
  );
}

// ─── Summary tab ────────────────────────────────────────────────────

function SummaryView({
  dailyData,
  chartSeries,
  dailyDetails,
  groupedRows,
  totalSeconds,
}: {
  dailyData: Array<{ label: string; hours: number }>;
  chartSeries: BarSeriesType[];
  dailyDetails: DayDetail[];
  groupedRows: Array<{
    key: string;
    label: string;
    totalSecs: number;
    billableSecs: number;
    percent: number;
  }>;
  totalSeconds: number;
}) {
  const { t } = useTranslation();
  const hasData = dailyData.some((d) => d.hours > 0);

  return (
    <Box>
      {/* Daily bar chart */}
      <Paper
        variant="outlined"
        sx={{ borderColor: colors.lightGray, borderRadius: 0, mt: 2, mb: 3, p: 0, overflow: 'hidden' }}
      >
        {hasData ? (
          <BarChart
            series={chartSeries}
            xAxis={[{ data: dailyData.map((d) => d.label), scaleType: 'band' }]}
            yAxis={[{ label: 'h' }]}
            height={240}
            hideLegend={chartSeries.length < 2}
            margin={{ top: 28, bottom: 40, left: 48, right: 16 }}
            slots={{
              tooltip: () => (
                <DayBreakdownTooltip details={dailyDetails} dayLabels={dailyData.map((d) => d.label)} />
              ),
            }}
          />
        ) : (
          <Box
            sx={{
              height: 220,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('kronos.report.empty.period')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Group table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderColor: colors.lightGray, borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: colors.bodyText }}>
                {groupedRows.length === 0 ? t('kronos.report.summaryCols.group') : t('kronos.report.summaryCols.title')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: colors.bodyText, width: 100 }}>
                {t('kronos.report.summaryCols.duration')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: colors.bodyText, width: 80 }}>
                {t('kronos.report.summaryCols.share')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: colors.bodyText, width: 120 }}>
                {t('kronos.report.summaryCols.billable')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.textMuted }}>
                  {t('kronos.report.empty.filtered')}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {groupedRows.map((row) => (
                  <TableRow
                    key={row.key}
                    sx={{ '&:hover': { bgcolor: colors.peach } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 3,
                            height: 32,
                            bgcolor: colors.yellow,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {formatHoursDecimal(row.totalSecs)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(row.percent)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {row.billableSecs > 0 ? (
                        <Typography variant="body2" sx={{ color: colors.softGreen.replace('#', '#') }}>
                          {formatHoursDecimal(row.billableSecs)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total row */}
                <TableRow sx={{ bgcolor: colors.lightGray }}>
                  <TableCell sx={{ fontWeight: 700, color: colors.bodyText }}>
                    {t('kronos.report.total')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatHoursDecimal(totalSeconds)}
                  </TableCell>
                  <TableCell align="right">100%</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatHoursDecimal(
                      groupedRows.reduce((s, r) => s + r.billableSecs, 0),
                    )}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Detailed tab ────────────────────────────────────────────────────

const DETAILED_PAGE_SIZE = 100;

function DetailedView({
  entries,
  projectsById,
  usersById,
  designationNameById,
  locale,
}: {
  entries: TimeEntry[];
  projectsById: ReadonlyMap<string, { code: string; name: string; clientId: string; allMemberUids: string[] }>;
  usersById: ReadonlyMap<string, string>;
  designationNameById: ReadonlyMap<string, string>;
  locale: string;
}) {
  const { t } = useTranslation();
  // ponytail: pagination côté client — 48k TableRow MUI d'un coup figeaient l'onglet
  // (2026-10-05). Les données restent toutes en mémoire ; l'export n'est pas paginé.
  const [page, setPage] = useState(0);
  const lastPage = Math.max(0, Math.ceil(entries.length / DETAILED_PAGE_SIZE) - 1);
  const safePage = Math.min(page, lastPage); // un filtre qui réduit la liste ne laisse pas une page vide
  const pageEntries = entries.slice(safePage * DETAILED_PAGE_SIZE, (safePage + 1) * DETAILED_PAGE_SIZE);
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderColor: colors.lightGray, borderRadius: 0, mt: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>{t('kronos.report.detailedCols.description')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t('kronos.report.detailedCols.project')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t('kronos.report.detailedCols.designation')}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t('kronos.report.detailedCols.user')}</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 110 }}>{t('kronos.report.detailedCols.date')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, width: 90 }}>{t('kronos.report.detailedCols.duration')}</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, width: 60 }}>{t('kronos.report.detailedCols.billable')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4, color: colors.textMuted }}>
                {t('kronos.report.empty.filtered')}
              </TableCell>
            </TableRow>
          ) : (
            pageEntries.map((e) => {
              const proj = projectsById.get(e.projectId);
              const started = e.startTime.toDate();
              const reason = nonWorkingDay(started);
              const dateStr = started.toLocaleDateString(locale, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });
              return (
                <TableRow
                  key={e.id}
                  sx={{
                    bgcolor: reason ? NON_WORKING_BG : undefined,
                    '&:hover': { bgcolor: colors.peach },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ color: e.description ? colors.bodyText : colors.textMuted }}>
                      {e.description || t('kronos.report.detailedCols.noDescription')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {proj ? (
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <Chip
                          label={proj.code}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '11px',
                            height: 20,
                            borderRadius: '4px',
                            bgcolor: colors.darkBg,
                            color: colors.yellow,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: colors.textMuted }}>
                          {proj.name}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{e.projectId}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {designationNameById.get(e.designationId) ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{usersById.get(e.userId) ?? e.userId}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {dateStr}
                    </Typography>
                    {reason && (
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ display: 'block', color: colors.textMuted }}
                      >
                        {reason.kind === 'holiday'
                          ? t('kronos.report.nonWorkingHoliday', { name: reason.name })
                          : t('kronos.report.nonWorkingWeekend')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {formatDuration(e.durationSeconds ?? 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: e.billable ? '#22c55e' : colors.lightGray,
                        mx: 'auto',
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {entries.length > DETAILED_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={entries.length}
          page={safePage}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={DETAILED_PAGE_SIZE}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) =>
            t('kronos.report.detailedPagination', { from, to, count })
          }
        />
      )}
    </TableContainer>
  );
}

// ─── Weekly tab ──────────────────────────────────────────────────────

function WeeklyView({
  weekDays,
  rows,
  totalSeconds,
  locale,
}: {
  weekDays: Date[];
  rows: Array<{ label: string; byDay: Map<number, number>; total: number }>;
  totalSeconds: number;
  locale: string;
}) {
  const { t } = useTranslation();
  const dayTotals = weekDays.map((d) =>
    rows.reduce((s, r) => s + (r.byDay.get(d.getTime()) ?? 0), 0),
  );

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderColor: colors.lightGray, borderRadius: 0, mt: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>{t('kronos.report.weeklyCols.group')}</TableCell>
            {weekDays.map((d) => {
              const reason = nonWorkingDay(d);
              return (
                <TableCell
                  key={d.getTime()}
                  align="right"
                  sx={{
                    fontWeight: 700,
                    width: 90,
                    bgcolor: reason ? NON_WORKING_BG : undefined,
                    color: reason ? colors.textMuted : undefined,
                  }}
                  title={reason?.kind === 'holiday' ? reason.name : undefined}
                >
                  {d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' })}
                  {reason?.kind === 'holiday' && (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ display: 'block', fontWeight: 400, color: colors.textMuted }}
                    >
                      {reason.name}
                    </Typography>
                  )}
                </TableCell>
              );
            })}
            <TableCell align="right" sx={{ fontWeight: 700, width: 90, bgcolor: colors.lightGray }}>
              {t('kronos.report.weeklyCols.total')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={weekDays.length + 2} align="center" sx={{ py: 4, color: colors.textMuted }}>
                {t('kronos.report.empty.weekly')}
              </TableCell>
            </TableRow>
          ) : (
            <>
              {rows.map((row) => (
                <TableRow key={row.label} sx={{ '&:hover': { bgcolor: colors.peach } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.label}
                    </Typography>
                  </TableCell>
                  {weekDays.map((d) => {
                    const secs = row.byDay.get(d.getTime()) ?? 0;
                    return (
                      <TableCell
                        key={d.getTime()}
                        align="right"
                        sx={{ bgcolor: nonWorkingDay(d) ? NON_WORKING_BG : undefined }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            color: secs > 0 ? colors.bodyText : colors.lightGray,
                            fontWeight: secs > 0 ? 600 : 400,
                          }}
                        >
                          {secs > 0 ? formatDuration(secs) : '—'}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ bgcolor: 'rgba(237,237,237,0.4)' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {formatDuration(row.total)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {/* Column totals */}
              <TableRow sx={{ bgcolor: colors.lightGray }}>
                <TableCell sx={{ fontWeight: 700 }}>{t('kronos.report.weeklyCols.total')}</TableCell>
                {dayTotals.map((secs, i) => (
                  <TableCell
                    key={i}
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: nonWorkingDay(weekDays[i]) ? colors.textMuted : undefined,
                    }}
                  >
                    {secs > 0 ? formatDuration(secs) : '—'}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                  {formatDuration(totalSeconds)}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
